import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, matchPath, useLocation } from 'react-router';

import { strReplaceChars } from '@fly/core';
import { NavMenuItem } from '@fly/shared';
import { FlySvgSprite } from '@fly/svg';
import classNames from 'classnames';

interface FlySubMenuElementListProps extends HTMLAttributes<HTMLDivElement> {
  items: Record<string, Array<NavMenuItem>>;
  onNavClick?: () => void;
  displayIcon?: boolean;
}

export const FlySubMenuElementList = ({
  items,
  onNavClick,
  displayIcon = true,
  className,
  ...rest
}: FlySubMenuElementListProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const getItemTestId = (item: NavMenuItem): string => {
    return strReplaceChars(
      t(item.title, { defaultValue: item.title }),
      ' ',
      '-'
    )?.toLocaleLowerCase?.();
  };
  return (
    <div {...rest} className={classNames('text-(--text-color)', className)}>
      {Object.entries(items).map(([key, value], index) => (
        <div key={index} data-testid={t(key, { defaultValue: key })}>
          {key && (
            <h5 className='font-bold flex items-center px-fly-3 py-fly-3 text-size-fly-2 capitalize opacity-(--text-opacity)'>
              {t(key, { defaultValue: key })}
            </h5>
          )}
          <ul className='flex w-full flex-col gap-fly-2'>
            {value.map((item: NavMenuItem) => {
              const isActive = !!matchPath(
                { path: item?.path || '', end: item.path === '/' },
                location.pathname
              );
              const activeClass = isActive
                ? 'border-(--fly-color-scheme-gray-4) bg-(--fly-color-scheme-gray-2) font-semibold opacity-100'
                : '';

              return (
                <div key={item.path} className='flex items-center gap-fly-5'>
                  <li
                    className={classNames(
                      'grow rounded-fly-3 pl-fly-4 text-size-fly-3 text-(--text-color) opacity-100 hover:bg-(--fly-color-scheme-gray-2)',
                      activeClass
                    )}
                    data-testid={`${getItemTestId(item)}`}
                  >
                    {item?.path && (
                      <NavLink
                        to={item.path!}
                        onClick={onNavClick}
                        className='flex h-full w-full items-center py-fly-3 text-size-fly-3 no-underline'
                      >
                        {item?.svgIcon && displayIcon && (
                          <FlySvgSprite
                            name={item.svgIcon!}
                            size={18}
                            className='fill-none'
                          />
                        )}
                        <span className='pl-fly-3'>
                          {t(item.title, { defaultValue: item.title })}
                        </span>
                      </NavLink>
                    )}
                    {!item?.path && (
                      <span className='pl-fly-3'>
                        {t(item.title, { defaultValue: item.title })}
                      </span>
                    )}
                  </li>
                </div>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};
